/**
 * SA-1410: a leaf-level rebase of a Customize session's unsaved edits onto the
 * overlay that is live now. Pure: no store, no DOM, inputs never mutated.
 *
 * A leaf is any value that is not a plain object (arrays, primitives, null).
 * `mine` is every leaf path where `current` differs from `start`. The result
 * starts from `fresh`; each of the person's changes is applied where `fresh`
 * still holds what `start` held, and otherwise `fresh` wins and the path is
 * reported as a conflict. Paths are dot-joined keys, the form the server's
 * findings and `describeOverlayPath` use.
 */
import type { WorkflowOverlayJson } from '@/types/workflowConfig'

export interface RebaseResult {
  overlay: WorkflowOverlayJson
  conflicts: string[]
}

type Json = Record<string, unknown>

function isPlainObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, i) => deepEqual(item, b[i]))
    )
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a)
    return (
      keys.length === Object.keys(b).length &&
      keys.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual(a[k], b[k]))
    )
  }
  return false
}

function clone<T>(value: T): T {
  return value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T)
}

/** Every leaf of `obj`, as [path, value], in document order. */
function leaves(obj: unknown, prefix: string[] = []): Array<[string[], unknown]> {
  if (!isPlainObject(obj)) return prefix.length > 0 ? [[prefix, obj]] : []
  return Object.entries(obj).flatMap(([k, v]) => leaves(v, [...prefix, k]))
}

/** The value at `path`, or undefined when any step is absent or not an object. */
function getAt(obj: unknown, path: string[]): unknown {
  let node: unknown = obj
  for (const key of path) {
    if (!isPlainObject(node) || !Object.prototype.hasOwnProperty.call(node, key)) return undefined
    node = node[key]
  }
  return node
}

function setAt(obj: Json, path: string[], value: unknown): void {
  let node = obj
  for (const key of path.slice(0, -1)) {
    if (!isPlainObject(node[key])) node[key] = {}
    node = node[key] as Json
  }
  node[path[path.length - 1]] = value
}

/** Delete the leaf at `path` and collapse parents left empty. */
function deleteAt(obj: Json, path: string[]): void {
  const [head, ...rest] = path
  if (rest.length === 0) {
    delete obj[head]
    return
  }
  const child = obj[head]
  if (!isPlainObject(child)) return
  deleteAt(child, rest)
  if (Object.keys(child).length === 0) delete obj[head]
}

export function rebaseOverlay(
  current: WorkflowOverlayJson,
  start: WorkflowOverlayJson,
  fresh: WorkflowOverlayJson,
): RebaseResult {
  const mine: Array<{ path: string[]; value: unknown; removed: boolean }> = []
  for (const [path, value] of leaves(current)) {
    if (!deepEqual(getAt(start, path), value)) mine.push({ path, value, removed: false })
  }
  for (const [path] of leaves(start)) {
    if (getAt(current, path) === undefined) mine.push({ path, value: undefined, removed: true })
  }

  const overlay = clone(fresh) as Json
  const conflicts: string[] = []
  for (const { path, value, removed } of mine) {
    if (deepEqual(getAt(fresh, path), getAt(start, path))) {
      if (removed) deleteAt(overlay, path)
      else setAt(overlay, path, clone(value))
    } else {
      conflicts.push(path.join('.'))
    }
  }
  return { overlay: overlay as WorkflowOverlayJson, conflicts }
}
